import { useCallback, useEffect, useRef, useState } from 'react';
import { tapPageReadResponseSchema, tapPageSaveResponseSchema, tapPagePublishResponseSchema } from '@inktap/contracts/tap-page';
import { sanitizeDocument, sanitizeTapProjectData, TAP_ACTION_TYPES, TAP_THEMES, type TapDocument, type TapProjectData } from '../../lib/tapDocument';

export type DraftState = {
  meta: { title: string; description: string; slug: string };
  projectData: TapProjectData; savedAt: string;
};
type Scope = { tenantId: string; subjectId: string; workspaceId: string; slugId: string };
type Base = { draftId: string | null; revision: number };
type Copy = { version: 'tap-device/v1'; scope: Scope; base: Base; state: DraftState };
type Owner = { scope: Scope; base: Base; slug: string };
type Review = { cloud: DraftState; device: Copy | null };
type Options = {
  endpoint: string; slug: string; session: { active: boolean; subjectId?: string; workspaceId?: string } | null;
  canSync: boolean; state: DraftState; editRevision: { current: number };
  hydrate: (state: DraftState) => void; setPublicSlug: (value: string | null) => void;
};
export const OWNED_DRAFT_KEY = 'inktap:tap-builder:owned:v1';
const stable = (value: unknown): unknown => Array.isArray(value) ? value.map(stable)
  : value !== null && typeof value === 'object' ? Object.fromEntries(Object.entries(value).filter(([, child]) => child !== undefined).sort(([a], [b]) => a.localeCompare(b)).map(([key, child]) => [key, stable(child)])) : value;
const same = (a: unknown, b: unknown) => JSON.stringify(stable(a)) === JSON.stringify(stable(b));
const fingerprint = (state: DraftState) => JSON.stringify([state.meta, state.projectData]);
const sameScope = (a: Scope, b: Scope) => a.tenantId === b.tenantId && a.subjectId === b.subjectId && a.workspaceId === b.workspaceId && a.slugId === b.slugId;
const record = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
// Device drafts include unfinished edits. Their structural model is deliberately
// independent of publication validation, so a blank field never strands a draft.
function isEditorDocument(value: unknown): value is TapDocument {
  if (!record(value) || !TAP_THEMES.some(theme => theme === value.theme) || !record(value.profile) || !Array.isArray(value.actions)) return false;
  const profile = value.profile;
  if (typeof profile.name !== 'string' || typeof profile.title !== 'string' ||
    !['subtitle', 'bio', 'photoUrl'].every(key => profile[key] === undefined || typeof profile[key] === 'string')) return false;
  const ids = new Set<string>();
  return value.actions.every(action => {
    if (!record(action) || typeof action.id !== 'string' || !action.id || ids.has(action.id) ||
      !TAP_ACTION_TYPES.some(type => type === action.type) ||
      !['label', 'url', 'provider'].every(key => action[key] === undefined || typeof action[key] === 'string')) return false;
    ids.add(action.id); return true;
  });
}
function parseCopy(raw: string | null): Copy | null {
  try {
    if (raw === null) return null;
    const copy = JSON.parse(raw) as Copy;
    if (copy?.version !== 'tap-device/v1' || !copy.scope || !['tenantId', 'subjectId', 'workspaceId', 'slugId'].every((key) => typeof copy.scope[key as keyof Scope] === 'string' && copy.scope[key as keyof Scope].length > 0) ||
      !copy.base || !(copy.base.draftId === null || typeof copy.base.draftId === 'string') || !Number.isSafeInteger(copy.base.revision) || copy.base.revision < 0 ||
      !copy.state?.meta || !['title', 'description', 'slug'].every((key) => typeof copy.state.meta[key as keyof DraftState['meta']] === 'string') ||
      typeof copy.state.savedAt !== 'string' || !Number.isFinite(Date.parse(copy.state.savedAt)) || copy.state.projectData?.version !== 'tap-app/v3' ||
      !isEditorDocument(copy.state.projectData.document)) return null;
    return copy;
  } catch { return null; }
}
// A failed or unconfirmed command is never replayed. Abort transport on every
// completion, including a deadline, and let the explicit read/review path recover.
async function requestJson(url: string, init: RequestInit = {}): Promise<unknown> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let parentAbort: (() => void) | undefined;
  const stopped = new Promise<never>((_resolve, reject) => {
    parentAbort = () => { controller.abort(); reject(new Error('Request stopped')); };
    if (init.signal?.aborted) parentAbort();
    else init.signal?.addEventListener('abort', parentAbort, { once: true });
    timer = setTimeout(() => { controller.abort(); reject(new Error('Request deadline')); }, 15000);
  });
  try {
    return await Promise.race([stopped, (async () => {
      const response = await fetch(url, { ...init, signal: controller.signal });
      if (!response.ok) throw new Error('Request not confirmed');
      const body = await response.text();
      if (body.length > 512 * 1024) throw new Error('Receipt too large');
      return JSON.parse(body) as unknown;
    })()]);
  } finally {
    clearTimeout(timer);
    if (parentAbort) init.signal?.removeEventListener('abort', parentAbort);
    controller.abort();
  }
}
export function useTapDraftSafety(options: Options) {
  const latest = useRef(options); latest.current = options;
  const [status, setStatus] = useState('Checking draft ownership. Device persistence is unconfirmed.');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [review, setReview] = useState<Review | null>(null);
  const [deviceIssue, setDeviceIssue] = useState<string | null>(null);
  const [legacyPresent, setLegacyPresent] = useState(false);
  const [paused, setPaused] = useState(false);
  const [deleteTarget, setDeleteTargetState] = useState<'owned' | 'legacy' | null>(null);
  const deletion = useRef<{ key: string; raw: string | null } | null>(null);
  const initialState = useRef(options.state);
  const [attempt, setAttempt] = useState<string | null>(null);
  const [lastConfirmed, setLastConfirmed] = useState<string | null>(null);
  const [readCount, setReadCount] = useState(0);
  const authority = useRef<Owner | null>(null);
  const cloudProtected = useRef<string | null>(null);
  const deviceProtected = useRef<string | null>(null);
  const storage = useRef<{ raw: string | null; known: boolean; blocked: boolean; paused: boolean }>({ raw: null, known: false, blocked: true, paused: false });
  const reviewing = useRef(false);
  const pendingHydration = useRef<string | null>(null);
  const phase = useRef<'idle' | 'read' | 'save' | 'publish' | 'unknown'>('read');
  const mounted = useRef(false);
  const generation = useRef(0);
  const target = JSON.stringify([options.endpoint, options.slug]);
  const observed = useRef(target);
  if (observed.current !== target) {
    observed.current = target; generation.current += 1; authority.current = null; storage.current.blocked = true; phase.current = 'read';
  }
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; generation.current += 1; }; }, []);
  const live = (epoch: number) => mounted.current && generation.current === epoch;
  const hydrate = useCallback((state: DraftState) => {
    pendingHydration.current = fingerprint(state); latest.current.hydrate(state);
  }, []);
  const writeDevice = useCallback((state: DraftState) => {
    const owner = authority.current; const slot = storage.current;
    if (!owner || slot.blocked || slot.paused || reviewing.current || pendingHydration.current !== null) return false;
    try {
      if (!slot.known || localStorage.getItem(OWNED_DRAFT_KEY) !== slot.raw) {
        slot.blocked = true; setDeviceIssue('Another tab changed the stored copy. Refresh and review before saving on this device.'); return false;
      }
      const raw = JSON.stringify({ version: 'tap-device/v1', scope: owner.scope, base: owner.base, state } satisfies Copy);
      localStorage.setItem(OWNED_DRAFT_KEY, raw); slot.raw = raw; deviceProtected.current = fingerprint(state); setDeviceIssue(null); return true;
    } catch { setDeviceIssue('Unable to save on this device. Your current edits remain in the editor.'); return false; }
  }, []);
  useEffect(() => {
    const epoch = ++generation.current; const controller = new AbortController();
    authority.current = null; phase.current = 'read'; reviewing.current = false;
    setReview(null); setReady(false); setBusy(false); storage.current.blocked = true; storage.current.known = false;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(OWNED_DRAFT_KEY); storage.current.raw = raw; storage.current.known = true;
      setLegacyPresent(localStorage.getItem('inktap:tap-builder:v3:' + options.slug.toLowerCase()) !== null);
    } catch { setDeviceIssue('Device storage is unavailable. Current edits remain in the editor.'); }
    const readRevision = latest.current.editRevision.current;
    setStatus('Checking draft ownership. You can keep editing; cloud commands wait for review.');
    if (!options.endpoint || !options.slug || !options.session) {
      phase.current = 'unknown'; setStatus('Sign in to confirm draft ownership. Current edits remain in memory; device persistence is unconfirmed.');
      return () => controller.abort();
    }
    void (async () => {
      try {
        const receipt = tapPageReadResponseSchema.parse(await requestJson(options.endpoint + '/pages/' + encodeURIComponent(options.slug), { credentials: 'include', cache: 'no-store', signal: controller.signal }));
        const current = latest.current; if (!live(epoch)) return;
        if (receipt.slug !== options.slug || (current.session?.subjectId && receipt.scope.subjectId !== current.session.subjectId) ||
          (current.session?.workspaceId && receipt.scope.workspaceId !== current.session.workspaceId)) throw new Error('Ownership mismatch');
        const owner = { scope: receipt.scope, slug: receipt.slug, base: { draftId: receipt.draft?.id ?? null, revision: receipt.draftRevision } };
        authority.current = owner; current.setPublicSlug(receipt.publicSlug);
        const page = receipt.draft;
        const document = page?.projectData?.version === 'tap-app/v3' ? sanitizeDocument(page.projectData.document, { actionLimit: null }) : null;
        if (page && !document) throw new Error('Draft unavailable');
        const cloud: DraftState = page && document ? { meta: { slug: receipt.slug, title: page.meta.title, description: page.meta.description ?? '' }, projectData: { version: 'tap-app/v3', document }, savedAt: page.updatedAt } : { ...initialState.current, meta: { ...initialState.current.meta, slug: receipt.slug } };
        cloudProtected.current = page ? fingerprint(cloud) : null;
        const copy = parseCopy(raw);
        const owned = copy && sameScope(copy.scope, receipt.scope) && copy.state.meta.slug === receipt.slug ? copy : null;
        const foreign = raw !== null && !owned;
        storage.current.blocked = foreign || !storage.current.known;
        setDeviceIssue(foreign ? 'The stored device copy has unverified ownership or an unsupported format. It is preserved and is not displayed. Delete it explicitly to enable device saving.' : null);
        const edited = current.editRevision.current !== readRevision || current.editRevision.current > 0;
        const divergent = owned !== null && (fingerprint(owned.state) !== fingerprint(cloud) || !same(owned.base, owner.base));
        phase.current = 'idle';
        if (edited || divergent || readCount > 0) {
          reviewing.current = true; setReview({ cloud, device: owned }); setStatus('Review the cloud draft and your retained work before saving or publishing.');
        } else {
          if (page) hydrate(cloud); setReady(true); setStatus('Draft ownership confirmed. Your editor is ready.');
        }
      } catch {
        if (!live(epoch)) return;
        authority.current = null; phase.current = 'unknown';
        setStatus('InkTap could not confirm draft ownership. Current edits remain in memory; device persistence is unconfirmed. Refresh and review to reconnect.');
      }
    })();
    return () => controller.abort();
  }, [target, readCount, hydrate]);
  useEffect(() => {
    const state = latest.current.state;
    if (pendingHydration.current !== null) {
      if (pendingHydration.current !== fingerprint(state)) return; pendingHydration.current = null;
    }
    if (ready && writeDevice(state) && phase.current === 'idle') setStatus('Draft saved on this device.');
  }, [options.state.meta, options.state.projectData.document, ready, writeDevice]);
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if ((event.storageArea !== null && event.storageArea !== localStorage) || (event.key !== null && event.key !== OWNED_DRAFT_KEY)) return;
      storage.current.blocked = true; setDeviceIssue('Another tab changed the stored copy. Refresh and review before saving on this device.');
    };
    window.addEventListener('storage', onStorage); return () => window.removeEventListener('storage', onStorage);
  }, []);
  useEffect(() => {
    const warnOnLeave = (event: BeforeUnloadEvent) => {
      if (latest.current.editRevision.current === 0) return;
      const current = fingerprint(latest.current.state);
      if (cloudProtected.current === current) return;
      try {
        const slot = storage.current;
        if (!slot.blocked && !slot.paused && deviceProtected.current === current && localStorage.getItem(OWNED_DRAFT_KEY) === slot.raw) return;
      } catch { /* An unreadable device slot cannot prove these edits persisted. */ }
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnOnLeave);
    return () => window.removeEventListener('beforeunload', warnOnLeave);
  }, []);
  const choose = (choice: 'current' | 'device' | 'cloud') => {
    if (!review || phase.current !== 'idle' || !authority.current) return;
    const next = choice === 'device' ? review.device?.state : choice === 'cloud' ? review.cloud : latest.current.state;
    if (!next) return;
    // A recovered device draft can differ from cloud even before any typing.
    // Treat that explicit choice as retained work for leave protection.
    if (choice === 'device') latest.current.editRevision.current += 1;
    reviewing.current = false; setReview(null); if (choice !== 'current') hydrate(next); setReady(true); setStatus('Review complete. Your chosen draft remains in the editor.');
  };
  const refresh = () => {
    if (phase.current === 'save' || phase.current === 'publish' || phase.current === 'read') return;
    authority.current = null; phase.current = 'read'; setReady(false); setReadCount((value) => value + 1);
  };
  const setDeleteTarget = (value: 'owned' | 'legacy' | null) => {
    if (value === null) { deletion.current = null; setDeleteTargetState(null); return; }
    const key = value === 'owned' ? OWNED_DRAFT_KEY : 'inktap:tap-builder:v3:' + latest.current.slug.toLowerCase();
    try { deletion.current = { key, raw: localStorage.getItem(key) }; setDeleteTargetState(value); }
    catch { setDeviceIssue('Device storage could not be read. No copy was deleted.'); }
  };
  const removeCopy = () => {
    if (!deleteTarget) return;
    try {
      if (!deletion.current || localStorage.getItem(deletion.current.key) !== deletion.current.raw) {
        setDeleteTarget(null); storage.current.blocked = true; setDeviceIssue('The stored copy changed before deletion. Review it again before deleting.'); return;
      }
      localStorage.removeItem(deletion.current.key);
      if (deleteTarget === 'owned') {
        storage.current = { raw: null, known: true, blocked: false, paused: true }; setPaused(true); setDeviceIssue(null);
      } else setLegacyPresent(false);
      setDeleteTarget(null); setStatus('Stored copy deleted. Current editor contents remain in memory.');
    } catch { setDeviceIssue('The stored copy could not be deleted. It remains on this device.'); }
  };
  const resume = () => {
    if (!authority.current || reviewing.current) return;
    storage.current.paused = false; setPaused(false);
    if (writeDevice(latest.current.state)) setStatus('Device saving resumed. Draft saved on this device.');
  };
  const saveSnapshot = async (): Promise<{ owner: Owner; state: DraftState; edit: number } | null> => {
    const current = latest.current; const owner = authority.current;
    if (!owner || reviewing.current || !ready || phase.current !== 'idle') {
      setStatus('Refresh and review the owned cloud draft before saving or publishing. Current edits remain in the editor.'); return null;
    }
    let projectData: TapProjectData;
    try { projectData = sanitizeTapProjectData(current.state.projectData, { actionLimit: null }); }
    catch { setStatus('This draft has invalid fields. Review validation before saving. Your current edits remain in the editor.'); return null; }
    const state = { ...current.state, meta: { ...current.state.meta, title: current.state.meta.title.trim() }, projectData, savedAt: new Date().toISOString() }; const deviceSaved = writeDevice(state);
    if (!current.canSync) {
      setStatus(deviceSaved ? 'Draft kept on this device. Order a card or upgrade to sync with InkTap.' : 'Device saving needs attention; current edits remain in memory.'); return null;
    }
    phase.current = 'save'; setBusy(true); setStatus('Saving to InkTap...');
    const epoch = generation.current; const edit = current.editRevision.current;
    try {
      const result = await requestJson(current.endpoint + '/pages/' + encodeURIComponent(owner.slug), {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: 'tap-page/v1', expected: { tenantId: owner.scope.tenantId, workspaceId: owner.scope.workspaceId, slugId: owner.scope.slugId, ...owner.base }, meta: { title: state.meta.title, description: state.meta.description }, projectData: state.projectData, savedAt: state.savedAt })
      });
      const receipt = tapPageSaveResponseSchema.parse(result); if (!live(epoch)) return null;
      if (!sameScope(receipt.scope, owner.scope) || receipt.slug !== owner.slug || receipt.draftRevision !== owner.base.revision + 1 ||
        !receipt.draft || (owner.base.draftId !== null && receipt.draft.id !== owner.base.draftId) ||
        receipt.draft.meta.title !== state.meta.title || (receipt.draft.meta.description ?? '') !== state.meta.description || !same(receipt.draft.projectData, state.projectData)) throw new Error('Mismatched save receipt');
      const acknowledged = { ...owner, base: { draftId: receipt.draft.id, revision: receipt.draftRevision } };
      authority.current = acknowledged; cloudProtected.current = fingerprint(state); current.setPublicSlug(receipt.publicSlug); phase.current = 'idle'; writeDevice(latest.current.state);
      setStatus(latest.current.editRevision.current === edit ? 'Saved to InkTap successfully.' : 'An earlier draft was saved to InkTap. Your newer edits still need to be saved.');
      return { owner: acknowledged, state, edit };
    } catch {
      if (live(epoch)) { phase.current = 'unknown'; setReady(false); setStatus('Saving was not confirmed. Do not repeat this command. Refresh and review the cloud draft before continuing; current edits remain in the editor.'); } return null;
    } finally { if (live(epoch)) setBusy(false); }
  };
  const save = async () => {
    if (phase.current === 'idle') await saveSnapshot(); else setStatus('Refresh and review before saving. Current edits remain in the editor.');
  };
  const publish = async () => {
    if (phase.current !== 'idle' || !latest.current.canSync) return;
    const saved = await saveSnapshot();
    if (!saved || phase.current !== 'idle' || latest.current.editRevision.current !== saved.edit || !authority.current || !sameScope(authority.current.scope, saved.owner.scope)) return;
    phase.current = 'publish'; setBusy(true); const epoch = generation.current; const requestId = crypto.randomUUID();
    setAttempt('Publication attempt started. Awaiting a confirmed receipt.'); setStatus('Publishing the acknowledged saved draft...');
    try {
      const result = await requestJson(latest.current.endpoint + '/pages/' + encodeURIComponent(saved.owner.slug) + '/publish', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: 'tap-page/v1', requestId, expected: { tenantId: saved.owner.scope.tenantId, workspaceId: saved.owner.scope.workspaceId, slugId: saved.owner.scope.slugId, ...saved.owner.base } })
      });
      const receipt = tapPagePublishResponseSchema.parse(result); if (!live(epoch)) return;
      if (!sameScope(receipt.scope, saved.owner.scope) || receipt.slug !== saved.owner.slug || receipt.publication.requestId !== requestId ||
        receipt.publication.sourceDraftId !== saved.owner.base.draftId || receipt.publication.sourceDraftRevision !== saved.owner.base.revision) throw new Error('Mismatched publication receipt');
      phase.current = 'idle'; latest.current.setPublicSlug(receipt.publicSlug); setAttempt(null);
      setLastConfirmed('Publication confirmed at ' + new Date(receipt.publication.publishedAt).toLocaleString() + '. ' + (receipt.publication.isCurrent ? 'This publication was current when acknowledged.' : 'A newer publication is current.'));
      setStatus(latest.current.editRevision.current === saved.edit ? 'Published to InkTap successfully.' : 'Publish completed, but you made newer edits. Review and publish them separately.');
    } catch {
      if (live(epoch)) {
        phase.current = 'unknown'; setReady(false); setAttempt('Publication outcome is unknown. The attempt will not be retried automatically.');
        setStatus('Publication was not confirmed. Refresh and review before starting a new attempt. Current edits remain in the editor.');
      }
    } finally { if (live(epoch)) setBusy(false); }
  };
  return { status, setStatus, busy, ready, review, choose, refresh, save, publish, deviceIssue, legacyPresent, paused, resume, deleteTarget, setDeleteTarget, removeCopy, attempt, lastConfirmed };
}
